/**
 * Postage Stamp Management
 * Handles postage stamp operations for Swarm storage
 */

import { getBee, getEffectiveBeeUrl } from './client'
import { logger } from '../../utils/logger'
import { getFdsIdClient, fdsIdEnabled } from '../fds-id'
import { createFairdropSIWEMessage } from '../wallet/siwe'
import type { PostageStamp, StampInfo } from '@/shared/types'

// Local Bee node URL
const LOCAL_BEE = 'http://localhost:1633'

// Sponsored stamp API endpoint for free tier
const SPONSOR_API = (import.meta.env.VITE_SPONSOR_API as string) || '/api/free-stamp'

interface StampsResponse {
  stamps?: PostageStamp[]
}

interface SponsoredStampResponse {
  batchId: string
  expiresAt: number
  depth: number
  totalCapacity: number
  remainingCapacity: number
  /** True if this is a shared/sponsored stamp (should NOT be saved as personal) */
  shared?: boolean
}

interface StampCapacity {
  depth: number
  bucketDepth: number
  totalChunks: number
  buckets: number
  usable: boolean
  exists: boolean
}

interface StorageCost {
  chunks: number
  blocks: number
  costPlur: number
  costBZZ: number
  days: number
}

/**
 * Check if local Bee node is available
 */
export async function isLocalBeeAvailable(): Promise<boolean> {
  try {
    const response = await fetch(`${LOCAL_BEE}/health`, {
      signal: AbortSignal.timeout(2000),
    })
    return response.ok
  } catch {
    return false
  }
}

/**
 * Top up an existing postage stamp via local Bee node
 * Adds BZZ to extend the stamp's lifetime
 * @param stampId - The batch ID to top up
 * @param amount - Amount in PLUR (1e-16 BZZ) to add
 */
export async function topUpStamp(stampId: string, amount: string): Promise<boolean> {
  if (!(await isLocalBeeAvailable())) {
    throw new Error('Local Bee node required for stamp top-up')
  }

  try {
    const response = await fetch(`${LOCAL_BEE}/stamps/topup/${stampId}/${amount}`, {
      method: 'PATCH',
      signal: AbortSignal.timeout(30000), // 30s for blockchain tx
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Top-up failed: ${error}`)
    }

    return true
  } catch (error) {
    logger.error('[Stamps] Top-up failed:', error)
    throw error
  }
}

/**
 * Get all available postage stamps from local Bee
 */
export async function getLocalStamps(): Promise<PostageStamp[]> {
  try {
    const response = await fetch(`${LOCAL_BEE}/stamps`, {
      signal: AbortSignal.timeout(3000),
    })
    if (!response.ok) return []
    const data = (await response.json()) as StampsResponse
    return data.stamps ?? []
  } catch {
    return []
  }
}

/**
 * Get all available postage stamps
 * Tries local Bee first, then falls back to configured Bee node
 */
export async function getAllStamps(): Promise<PostageStamp[]> {
  // Try local Bee first
  if (await isLocalBeeAvailable()) {
    const localStamps = await getLocalStamps()
    if (localStamps.length > 0) {
      return localStamps.filter((s) => s.usable)
    }
  }

  // Fall back to configured Bee node
  const bee = getBee()
  try {
    const stamps = await bee.getAllPostageBatch()
    // Convert bee-js PostageBatch to our PostageStamp type
    return stamps
      .filter((s) => s.usable)
      .map((s) => ({
        batchID: String(s.batchID),
        utilization: s.utilization,
        usable: s.usable,
        label: s.label,
        depth: s.depth,
        amount: s.amount,
        bucketDepth: s.bucketDepth,
        blockNumber: s.blockNumber,
        immutableFlag: s.immutableFlag,
      })) as PostageStamp[]
  } catch (error) {
    logger.error('[Stamps] Failed to get stamps:', error)
    return []
  }
}

/**
 * Get a specific stamp by ID.
 * Tries Bee API first, falls back to fds-id server for user-owned on-chain stamps
 * (Bee light mode only tracks its own stamps).
 */
export async function getStamp(stampId: string): Promise<PostageStamp | null> {
  // Try local Bee first
  if (await isLocalBeeAvailable()) {
    try {
      const response = await fetch(`${LOCAL_BEE}/stamps/${stampId}`, {
        signal: AbortSignal.timeout(3000),
      })
      if (response.ok) {
        return (await response.json()) as PostageStamp
      }
    } catch {
      // Fall through to configured Bee
    }
  }

  // Fall back to configured Bee node
  const beeUrl = getEffectiveBeeUrl()
  try {
    const response = await fetch(`${beeUrl}/stamps/${stampId}`, {
      signal: AbortSignal.timeout(5000),
    })
    if (response.ok) {
      return (await response.json()) as PostageStamp
    }
  } catch {
    // Fall through to fds-id
  }

  // Fall back to fds-id server (checks on-chain for user-owned stamps)
  if (fdsIdEnabled()) {
    try {
      const client = getFdsIdClient()
      const status = await client.pollStampStatus(stampId)
      if (status.exists) {
        return {
          batchID: stampId,
          usable: status.usable,
          depth: status.depth ?? 20,
          amount: status.amount ?? '0',
          bucketDepth: 16,
          blockNumber: 0,
          batchTTL: status.batchTTL ?? 0,
          utilization: 0,
          immutableFlag: false,
        } as PostageStamp
      }
    } catch {
      // All fallbacks exhausted
    }
  }

  logger.error('[Stamps] Stamp not found via Bee or fds-id:', stampId.slice(0, 16))
  return null
}

/**
 * Check if a stamp is usable
 */
export async function isStampUsable(stampId: string): Promise<boolean> {
  const stamp = await getStamp(stampId)
  return stamp?.usable ?? false
}

/**
 * Get stamp capacity info
 */
export async function getStampCapacity(stampId: string): Promise<StampCapacity | null> {
  const stamp = await getStamp(stampId)
  if (!stamp) {
    return null
  }

  const depth = stamp.depth
  const bucketDepth = stamp.bucketDepth || 16
  const totalChunks = Math.pow(2, depth)
  const chunksPerBucket = Math.pow(2, bucketDepth)
  const buckets = totalChunks / chunksPerBucket

  return {
    depth,
    bucketDepth,
    totalChunks,
    buckets,
    usable: stamp.usable,
    exists: true,
  }
}

/**
 * Calculate remaining capacity from stamp properties
 * Formula: totalCapacity * (1 - utilization/100)
 * Where totalCapacity = 2^depth * 4096 bytes
 */
function calculateRemainingCapacity(stamp: PostageStamp): number {
  const totalCapacity = Math.pow(2, stamp.depth) * 4096
  const usedPercentage = stamp.utilization / 100
  return Math.floor(totalCapacity * (1 - usedPercentage))
}

/**
 * Get stamp info for display in settings
 */
export async function getStampInfo(stampId: string): Promise<StampInfo | null> {
  try {
    const stamp = await getStamp(stampId)
    if (!stamp) return null

    return {
      batchID: stamp.batchID,
      expiresAt: stamp.batchTTL ? Date.now() + stamp.batchTTL * 1000 : 0,
      remainingCapacity: calculateRemainingCapacity(stamp),
      usable: stamp.usable,
      depth: stamp.depth,
      utilization: stamp.utilization,
    }
  } catch (error) {
    logger.warn('[Stamps] Failed to get stamp info:', error)
    return null
  }
}

interface RequestSponsoredStampOptions {
  accountId?: string
}

/**
 * Request a sponsored stamp for free tier uploads
 */
export async function requestSponsoredStamp(
  options: RequestSponsoredStampOptions = {}
): Promise<SponsoredStampResponse> {
  try {
    const response = await fetch(SPONSOR_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        timestamp: Date.now(),
        accountId: options.accountId,
      }),
    })

    if (!response.ok) {
      throw new Error(`Sponsor API error: ${response.status}`)
    }

    const data = (await response.json()) as SponsoredStampResponse
    data.shared = true // Mark as shared — must not be saved as personal stamp
    return data
  } catch (error) {
    logger.error('[Stamps] Failed to get sponsored stamp:', error)
    throw new Error('Free tier currently unavailable. Please try again later.')
  }
}

/**
 * Calculate cost for storage duration
 */
export function calculateStorageCost(fileSize: number, days = 7): StorageCost {
  const bytesPerChunk = 4096
  const chunks = Math.ceil(fileSize / bytesPerChunk)

  // Price per chunk per block (in PLUR, smallest unit)
  const pricePerChunkPerBlock = 24000
  const blocksPerDay = 4 * 60 * 24 // ~4 blocks per minute

  const totalBlocks = days * blocksPerDay
  const cost = chunks * pricePerChunkPerBlock * totalBlocks

  // Convert to BZZ (1 BZZ = 10^16 PLUR)
  const costInBZZ = cost / Math.pow(10, 16)

  return {
    chunks,
    blocks: totalBlocks,
    costPlur: cost,
    costBZZ: costInBZZ,
    days,
  }
}

/**
 * Estimate stamp depth needed for file size
 */
export function estimateDepth(fileSize: number): number {
  const bytesPerChunk = 4096
  const chunks = Math.ceil(fileSize / bytesPerChunk)

  // Depth determines total addressable chunks (2^depth)
  // Add some buffer for metadata and future use
  const requiredDepth = Math.ceil(Math.log2(chunks * 1.5))

  // Minimum depth is 17 for practical use
  return Math.max(17, requiredDepth)
}

/**
 * Request a stamp for a new account using fds-id trial stamp API.
 * The stamp is owned by the account's Ethereum address (derived from keypair).
 * Falls back to sponsored stamp if fds-id is unavailable.
 *
 * @param privateKeyHex - Account's private key (hex, no 0x prefix)
 * @param accountId - Account username for fallback sponsor API
 */
export async function requestAccountStamp(
  privateKeyHex: string,
  accountId: string
): Promise<SponsoredStampResponse> {
  // Try fds-id trial stamp first
  if (fdsIdEnabled()) {
    try {
      const { Wallet } = await import('ethers')
      const key = privateKeyHex.startsWith('0x') ? privateKeyHex : `0x${privateKeyHex}`
      const wallet = new Wallet(key)
      const address = wallet.address

      // Create and sign SIWE message
      const { message } = await createFairdropSIWEMessage(address, 1)
      const signature = await wallet.signMessage(message)

      // Request trial stamp from fds-id server
      const client = getFdsIdClient()
      const trial = await client.requestTrialStamp(message, signature)

      if (trial.stampId && !trial.error) {
        logger.info('[Stamps] Trial stamp received from fds-id:', trial.stampId.slice(0, 12))
        return {
          batchId: trial.stampId,
          expiresAt: trial.expiresAt ?? Date.now() + 7 * 24 * 60 * 60 * 1000,
          depth: 20,
          totalCapacity: trial.maxBytes ?? Math.pow(2, 20) * 4096,
          remainingCapacity: trial.maxBytes ?? Math.pow(2, 20) * 4096,
        }
      }

      // Trial already used or error - fall through to sponsor
      if (trial.error) {
        logger.warn('[Stamps] fds-id trial stamp error:', trial.error)
      }
    } catch (error) {
      logger.warn('[Stamps] fds-id trial stamp failed, falling back to sponsor:', error)
    }
  }

  // Fallback: sponsored stamp
  return requestSponsoredStamp({ accountId })
}
